<script setup lang="ts">
import { LucideCheck } from "lucide-vue-next"
import {
  CheckboxIndicator,
  CheckboxRoot,
  CheckboxRootEmits,
  type CheckboxRootProps,
  useForwardPropsEmits,
} from "reka-ui"

export interface UiCheckboxClasses {
  root?: ClassValue
  label?: ClassValue
  indicator?: ClassValue
  icon?: ClassValue
  control?: {
    root?: ClassValue
    checked?: ClassValue
    unchecked?: ClassValue
  }
}

export interface UiCheckboxProps extends CheckboxRootProps {
  label?: string
  id?: string
  disabled?: boolean
  name?: string
  class?: string
  classes?: UiCheckboxClasses
  variants?: {
    checked?: "default" | "inverted"
    unchecked?: "default" | "mixed"
  }
  size?: "default" | "small"
}

const props = defineProps<UiCheckboxProps>()
const emits = defineEmits<CheckboxRootEmits>()
const slots = useSlots()

const delegatedProps = computed(() => {
  const { class: _, classes, ...delegated } = props
  return delegated
})

const forwardedProps = useForwardPropsEmits(delegatedProps, emits)

const classNames = computed(() => ({
  root: cn(
    "gap-xxs flex cursor-pointer",
    { "cursor-not-allowed opacity-50": props.disabled },
    props.class,
    props.classes?.root
  ),
  label: cn("-translate-y-px select-none font-light", props.classes?.label),
  indicator: cn("flex items-center justify-center text-current", props.classes?.indicator),
  control: {
    root: cn(
      "focus-visible:ring-ring ring-offset-background flex aspect-square size-5 items-center justify-center rounded-sm border shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed",
      { "size-3.5": props.size === "small" },
      props.modelValue ? props.classes?.control?.checked : props.classes?.control?.unchecked,
      props.classes?.control?.root
    ),
    checked: cn("bg-primary border-primary text-primary-foreground", props.classes?.control?.checked),
    unchecked: cn("border-black/10 bg-white", props.classes?.control?.unchecked),
  },
  icon: cn("size-4", { "size-3": props.size === "small" }, props.classes?.icon),
}))
</script>

<template>
  <label :class="classNames.root">
    <CheckboxRoot
      v-bind="forwardedProps"
      :id="props.id"
      :class="
        cn(
          classNames.control.root,
          props.modelValue ? classNames.control.checked : classNames.control.unchecked
        )
      "
    >
      <CheckboxIndicator :class="classNames.indicator">
        <LucideCheck v-if="props.modelValue" :class="classNames.icon" stroke-width="3px" />
      </CheckboxIndicator>
    </CheckboxRoot>

    <span v-if="slots['label'] || props.label" :class="classNames.label">
      <slot v-bind="{ classes: classNames.label }" name="label">
        {{ props.label }}
      </slot>
    </span>
  </label>
</template>
